import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { 
  CheckCircle2, 
  XCircle, 
  Clock,
  Eye,
  Search,
  Receipt,
  MapPin,
  Calendar,
  FileText,
  RefreshCw,
  Check,
  X,
  Banknote,
  Download
} from "lucide-react";

export default function AdminPaymentConfirmations() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [stats, setStats] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewingPayment, setViewingPayment] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [activeTab, setActiveTab] = useState("pending");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, pendingRes, allRes] = await Promise.all([
        axios.get(`${API}/admin/payments/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/payments/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/payments/all?limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setPendingPayments(pendingRes.data);
      setAllPayments(allRes.data.payments);
    } catch (error) {
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (paymentId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/payments/${paymentId}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment confirmed! Next payment date updated.");
      fetchData();
      setViewingPayment(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm payment");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append("reason", rejectionReason);
      
      await axios.post(`${API}/admin/payments/${rejectDialog.id}/reject`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment rejected.");
      fetchData();
      setRejectDialog(null);
      setRejectionReason("");
      setViewingPayment(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject payment");
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return `₦${amount}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "confirmed":
        return (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Confirmed
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  const filteredPayments = (activeTab === "pending" ? pendingPayments : allPayments).filter(payment =>
    payment.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.reference_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const PaymentCard = ({ payment, showActions = false }) => (
    <div 
      className="bg-stone-50 rounded-xl p-4 border border-stone-200 hover:shadow-md transition-shadow"
      data-testid={`payment-card-${payment.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Receipt className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-primary">{payment.tenant_name}</h3>
            <p className="text-xs text-text-muted">@{payment.username}</p>
          </div>
        </div>
        {getStatusBadge(payment.status)}
      </div>

      <div className="space-y-2 text-sm mb-4">
        <div className="flex items-center justify-between">
          <span className="text-text-muted flex items-center gap-1">
            <Banknote className="w-4 h-4" />
            Amount
          </span>
          <span className="font-bold text-green-600">{formatCurrency(payment.amount)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-text-muted flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            Payment Date
          </span>
          <span>{formatDate(payment.payment_date)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-text-muted flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            Location
          </span>
          <span>{payment.location} - Apt #{payment.apartment_number}</span>
        </div>
        {payment.reference_number && (
          <div className="flex items-center justify-between">
            <span className="text-text-muted">Reference</span>
            <span className="font-mono text-xs">{payment.reference_number}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-stone-200">
        <span className="text-xs text-text-muted">
          Submitted: {formatDate(payment.submitted_at)}
        </span>
        <div className="flex gap-2">
          {payment.status === "confirmed" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/payments/${payment.id}/receipt`, '_blank')}
              className="text-green-600 border-green-200 hover:bg-green-50"
              data-testid={`download-receipt-btn-${payment.id}`}
            >
              <Download className="w-4 h-4 mr-1" />
              Receipt
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewingPayment(payment)}
            data-testid={`view-payment-btn-${payment.id}`}
          >
            <Eye className="w-4 h-4 mr-1" />
            View
          </Button>
          {showActions && payment.status === "pending_confirmation" && (
            <>
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleConfirm(payment.id)}
                disabled={processing}
                data-testid={`confirm-payment-btn-${payment.id}`}
              >
                <Check className="w-4 h-4 mr-1" />
                Confirm
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setRejectDialog(payment)}
                disabled={processing}
                data-testid={`reject-payment-btn-${payment.id}`}
              >
                <X className="w-4 h-4 mr-1" />
                Reject
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-payment-confirmations">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary flex items-center gap-3">
                <Receipt className="w-8 h-8" />
                Payment Confirmations
              </h1>
              <p className="text-text-muted mt-1">Review and confirm tenant payment submissions</p>
            </div>
            <Button
              variant="outline"
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-payments-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="border-amber-200 bg-amber-50/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                      <p className="text-xs text-text-muted">Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-stone-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{stats.confirmed}</p>
                      <p className="text-xs text-text-muted">Confirmed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-stone-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                      <p className="text-xs text-text-muted">Rejected</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-stone-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Banknote className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-blue-600">
                        ₦{stats.total_confirmed_amount?.toLocaleString() || 0}
                      </p>
                      <p className="text-xs text-text-muted">Total Confirmed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Search */}
          <div className="relative w-full md:w-80 mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              data-testid="search-payments-input"
              placeholder="Search by name, location, or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 bg-white border-stone-200"
            />
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="pending" data-testid="pending-tab">
                Pending ({pendingPayments.length})
              </TabsTrigger>
              <TabsTrigger value="all" data-testid="all-tab">
                All Payments ({allPayments.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-pulse text-primary">Loading...</div>
                </div>
              ) : filteredPayments.length === 0 ? (
                <Card className="border-stone-200">
                  <CardContent className="py-12 text-center">
                    <CheckCircle2 className="w-12 h-12 text-green-300 mx-auto mb-4" />
                    <p className="text-text-muted">No pending payments to review</p>
                    <p className="text-sm text-stone-400 mt-1">All payments have been processed!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredPayments.map((payment) => (
                    <PaymentCard key={payment.id} payment={payment} showActions={true} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="all">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-pulse text-primary">Loading...</div>
                </div>
              ) : filteredPayments.length === 0 ? (
                <Card className="border-stone-200">
                  <CardContent className="py-12 text-center">
                    <Receipt className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                    <p className="text-text-muted">No payments found</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredPayments.map((payment) => (
                    <PaymentCard key={payment.id} payment={payment} showActions={false} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* View Payment Details Dialog */}
      <Dialog open={!!viewingPayment} onOpenChange={() => setViewingPayment(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
            <DialogDescription>
              Complete payment information submitted by tenant
            </DialogDescription>
          </DialogHeader>
          
          {viewingPayment && (
            <div className="space-y-6 py-4">
              {/* Status Banner */}
              <div className={`p-4 rounded-xl ${
                viewingPayment.status === 'confirmed' ? 'bg-green-50 border border-green-200' :
                viewingPayment.status === 'rejected' ? 'bg-red-50 border border-red-200' :
                'bg-amber-50 border border-amber-200'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Status</span>
                  {getStatusBadge(viewingPayment.status)}
                </div>
                {viewingPayment.rejection_reason && (
                  <p className="text-sm text-red-700 mt-2">
                    <strong>Reason:</strong> {viewingPayment.rejection_reason}
                  </p>
                )}
              </div>

              {/* Tenant Info */}
              <div className="bg-stone-50 rounded-xl p-4">
                <h3 className="font-semibold text-primary mb-3">Tenant Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-muted">Name</span>
                    <p className="font-medium">{viewingPayment.tenant_name}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Username</span>
                    <p className="font-medium">@{viewingPayment.username}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Location</span>
                    <p className="font-medium">{viewingPayment.location}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Apartment</span>
                    <p className="font-medium">#{viewingPayment.apartment_number}</p>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-stone-50 rounded-xl p-4">
                <h3 className="font-semibold text-primary mb-3">Payment Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-muted">Amount</span>
                    <p className="font-bold text-green-600 text-lg">{formatCurrency(viewingPayment.amount)}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Payment Date</span>
                    <p className="font-medium">{formatDate(viewingPayment.payment_date)}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Reference Number</span>
                    <p className="font-medium font-mono">{viewingPayment.reference_number || "-"}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Payment Frequency</span>
                    <p className="font-medium">{viewingPayment.payment_frequency}</p>
                  </div>
                </div>
                {viewingPayment.notes && (
                  <div className="mt-4 pt-4 border-t border-stone-200">
                    <span className="text-text-muted text-sm">Notes</span>
                    <p className="text-sm mt-1 p-2 bg-white rounded">{viewingPayment.notes}</p>
                  </div>
                )}
              </div>

              {/* Payment Proof */}
              <div className="bg-stone-50 rounded-xl p-4">
                <h3 className="font-semibold text-primary mb-3">Payment Proof</h3>
                {viewingPayment.proof_url && (
                  viewingPayment.proof_url.endsWith('.pdf') ? (
                    <a 
                      href={`${process.env.REACT_APP_BACKEND_URL}${viewingPayment.proof_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-primary hover:underline p-4 bg-white rounded-lg border border-stone-200"
                    >
                      <FileText className="w-6 h-6" />
                      <span>View PDF Document</span>
                    </a>
                  ) : (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${viewingPayment.proof_url}`}
                      alt="Payment Proof"
                      className="max-w-full h-auto rounded-lg border border-stone-200"
                    />
                  )
                )}
              </div>

              {/* Timestamps */}
              <div className="text-xs text-text-muted">
                <p>Submitted: {formatDate(viewingPayment.submitted_at)}</p>
                {viewingPayment.confirmed_at && (
                  <p>Processed: {formatDate(viewingPayment.confirmed_at)}</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setViewingPayment(null)}>
                  Close
                </Button>
                {viewingPayment.status === "confirmed" && (
                  <Button
                    variant="outline"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/payments/${viewingPayment.id}/receipt`, '_blank')}
                    className="text-green-600 border-green-200 hover:bg-green-50"
                    data-testid="download-receipt-dialog-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Receipt
                  </Button>
                )}
                {viewingPayment.status === "pending_confirmation" && (
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        setRejectDialog(viewingPayment);
                      }}
                      disabled={processing}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleConfirm(viewingPayment.id)}
                      disabled={processing}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Confirm Payment
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={() => {
        setRejectDialog(null);
        setRejectionReason("");
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Payment</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this payment
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
              data-testid="rejection-reason-input"
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => {
              setRejectDialog(null);
              setRejectionReason("");
            }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={processing || !rejectionReason.trim()}
              data-testid="confirm-reject-btn"
            >
              {processing ? "Rejecting..." : "Reject Payment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
